import {
  createFrontendModule,
  ApiBlueprint,
  createApiFactory,
  discoveryApiRef,
  configApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/frontend-plugin-api';
import {
  convertLegacyEntityCardExtension,
  convertLegacyEntityContentExtension,
  EntityCardBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  EntityJiraOverviewCard,
  isJiraAvailable,
  jiraApiRef,
  JiraAPI,
} from '@roadiehq/backstage-plugin-jira';
import {
  EntityAmazonEcrImagesContent,
  isAmazonEcrAvailable,
} from '@aws/amazon-ecr-plugin-for-backstage';
import {
  EntityAmazonEcsServicesContent,
  isAmazonEcsServiceAvailable,
} from '@aws/amazon-ecs-plugin-for-backstage';
import { EntityBadgesDialog } from '@backstage-community/plugin-badges';
import {
  EntityKwirthLogContent,
  KwirthLogClient,
  kwirthLogApiRef,
} from '@jfvilas/plugin-kwirth-log';
import { isKwirthAvailable } from '@jfvilas/plugin-kwirth-common';
import {
  EntityAWSLambdaOverviewCard,
  isAWSLambdaAvailable,
  awsLambdaApiRef,
  AwsLambdaClient,
} from '@roadiehq/backstage-plugin-aws-lambda';
import {
  EntityEndOfLifeCard,
  isEndOfLifeAvailable,
  endOfLifeApiRef,
  EndOfLifeClient,
} from '@dweber019/backstage-plugin-endoflife';
import { ComponentType, useState } from 'react';
import { Button } from '@material-ui/core';
// ECR/ECS não exportam o apiRef nem o client no entrypoint público (só o
// Content component) — sem registrar o ApiFactory manualmente, a aba quebra
// em runtime com "No implementation available for apiRef{...}" (achado
// 2026-07-13 testando a aba ECS num Component real; ECR tinha o mesmo bug,
// nunca exercitado até então). Import direto do arquivo interno, sem .d.ts
// próprio — daí o @ts-expect-error.
// @ts-expect-error - deep import sem .d.ts, só existe no rollup do index
import { amazonEcrApiRef } from '@aws/amazon-ecr-plugin-for-backstage/dist/api/AwsEcrApi.esm.js';
// @ts-expect-error - deep import sem .d.ts, só existe no rollup do index
import { AwsEcrClient } from '@aws/amazon-ecr-plugin-for-backstage/dist/api/AwsEcrClient.esm.js';
// @ts-expect-error - deep import sem .d.ts, só existe no rollup do index
import { amazonEcsApiRef } from '@aws/amazon-ecs-plugin-for-backstage/dist/api/AmazonEcsApi.esm.js';
// @ts-expect-error - deep import sem .d.ts, só existe no rollup do index
import { AmazonEcsApiClient } from '@aws/amazon-ecs-plugin-for-backstage/dist/api/AmazonEcsApiClient.esm.js';
// badges tem o mesmo problema — apiRef/client não exportados no entrypoint
// público, só EntityBadgesDialog (achado 2026-07-13, "No implementation
// available for apiRef{plugin.badges.client}" em prod).
// @ts-expect-error - deep import sem .d.ts, só existe no rollup do index
import { badgesApiRef } from '@backstage-community/plugin-badges/dist/api/types.esm.js';
// @ts-expect-error - deep import sem .d.ts, só existe no rollup do index
import { BadgesClient } from '@backstage-community/plugin-badges/dist/api/BadgesClient.esm.js';

// Roadie Jira e AWS Labs ECR só publicam plugin no formato antigo (sem
// entrypoint /alpha) — convertLegacyEntity*Extension (@backstage/plugin-catalog-react/alpha)
// envolve o componente legado direto num blueprint do novo sistema, sem
// precisar de EntityPage.tsx customizado. Ver docs/operacao.md.

const jiraApi = ApiBlueprint.make({
  name: 'jira',
  params: define =>
    define(
      createApiFactory({
        api: jiraApiRef,
        deps: { discoveryApi: discoveryApiRef, configApi: configApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, configApi, fetchApi }) =>
          new JiraAPI({ discoveryApi, configApi, fetchApi }),
      }),
    ),
});

const jiraOverviewCard = convertLegacyEntityCardExtension(EntityJiraOverviewCard, {
  name: 'jira-overview',
  filter: isJiraAvailable,
  // sem `type` o layout padrão da overview não sabe em qual grid encaixar
  // o card e descarta ele silenciosamente — 'content' = grid principal
  // (mesma área de "Depends on components"), não a coluna estreita 'info'.
  type: 'content',
});

// ponytail: Router interno do EntityAmazonEcrImagesContent usa createRouteRef
// próprio do plugin legado pro link de detalhe de scan de imagem — pode não
// resolver por não estarmos registrando o plugin inteiro, só o componente.
// Testar quando o primeiro Component for anotado; se quebrar, promover pra
// EntityContentLayoutBlueprint com bind explícito da route.
const ecrImagesContent = convertLegacyEntityContentExtension(EntityAmazonEcrImagesContent, {
  name: 'amazon-ecr-images',
  filter: isAmazonEcrAvailable,
  path: '/ecr',
  title: 'Amazon ECR',
});

const ecrApi = ApiBlueprint.make({
  name: 'amazon-ecr',
  params: define =>
    define(
      createApiFactory({
        api: amazonEcrApiRef,
        deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
        factory: ({ discoveryApi, identityApi }) =>
          new AwsEcrClient({ discoveryApi, identityApi }),
      }),
    ),
});

const ecsServicesContent = convertLegacyEntityContentExtension(EntityAmazonEcsServicesContent, {
  name: 'amazon-ecs-services',
  filter: isAmazonEcsServiceAvailable,
  path: '/ecs',
  title: 'Amazon ECS',
});

const ecsApi = ApiBlueprint.make({
  name: 'amazon-ecs',
  params: define =>
    define(
      createApiFactory({
        api: amazonEcsApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new AmazonEcsApiClient({ discoveryApi, fetchApi }),
      }),
    ),
});

// EntityBadgesDialog é só o modal (open/onClose) — o pacote não expõe um
// botão pra abri-lo, então embrulha os dois num componente próprio (mesmo
// padrão de convertLegacyEntity*Extension pros demais legados).
const BadgesButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outlined" size="small" onClick={() => setOpen(true)}>
        Ver badges
      </Button>
      <EntityBadgesDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

// convertLegacyEntityCardExtension só funciona com componentes criados via
// createComponentExtension do sistema legado (lê metadata anexada por essa
// API) — BadgesButton é um componente React qualquer, escrito por nós, sem
// essa metadata. Usar convertLegacyEntityCardExtension nele lança "Extension
// has no name" NA HORA DO BOOT (o array de extensions é avaliado eager no
// load do módulo) e derruba o app inteiro, não só o card — achado em prod
// 2026-07-13 (catalog.apoia.se ficou em branco). EntityCardBlueprint.make é
// o primitivo que convertLegacyEntityCardExtension usa por baixo; aceita
// qualquer componente direto, sem exigir a metadata legada.
const badgesCard = EntityCardBlueprint.make({
  name: 'badges',
  params: {
    type: 'info',
    loader: async () => <BadgesButton />,
  },
});

const badgesApi = ApiBlueprint.make({
  name: 'badges',
  params: define =>
    define(
      createApiFactory({
        api: badgesApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef, configApi: configApiRef },
        factory: ({ discoveryApi, fetchApi, configApi }) =>
          BadgesClient.fromConfig({ discoveryApi, fetchApi, configApi }),
      }),
    ),
});

const awsLambdaApi = ApiBlueprint.make({
  name: 'aws-lambda',
  params: define =>
    define(
      createApiFactory({
        api: awsLambdaApiRef,
        deps: {},
        factory: () => new AwsLambdaClient(),
      }),
    ),
});

// roleArn: apoiase-backstage-lambda-reader (role dedicada, só
// lambda:GetFunction/GetFunctionConfiguration — ver comentário em
// packages/backend/src/index.ts). O card manda essa ARN pro backend trocar
// por credencial STS temporária vendida ao browser; sem isso o backend
// cairia no default sts:GetSessionToken, herdando o escopo completo do
// sa_automation_ro.
const LambdaOverviewCard = () => (
  <EntityAWSLambdaOverviewCard roleArn="arn:aws:iam::106572496308:role/apoiase-backstage-lambda-reader" />
);

// Mesmo motivo do badgesCard: LambdaOverviewCard é um wrapper nosso (só pra
// injetar o prop roleArn), não um Extension legado — precisa de
// EntityCardBlueprint.make direto, não do convertLegacyEntityCardExtension.
const lambdaOverviewCard = EntityCardBlueprint.make({
  name: 'aws-lambda-overview',
  params: {
    // isAWSLambdaAvailable retorna string | undefined (a annotation em si),
    // não boolean — filter exige boolean.
    filter: entity => Boolean(isAWSLambdaAvailable(entity)),
    type: 'content',
    loader: async () => <LambdaOverviewCard />,
  },
});

// endoflife exporta tudo publicamente (apiRef + client no entrypoint
// principal, sem deep import) — único dos plugins legacy instalados até
// agora que não tem o bug do apiRef ausente. baseUrl aponta direto pra API
// pública endoflife.date; discoveryApi/fetchApi/identityApi só são usados
// pelo método getFromSource (annotation source-location), que não usamos.
const endOfLifeApi = ApiBlueprint.make({
  name: 'endoflife',
  params: define =>
    define(
      createApiFactory({
        api: endOfLifeApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef, identityApi: identityApiRef },
        factory: ({ discoveryApi, fetchApi, identityApi }) =>
          new EndOfLifeClient({
            baseUrl: 'https://endoflife.date',
            discoveryApi,
            fetchApi,
            identityApi,
          }),
      }),
    ),
});

// EntityEndOfLifeCard usa roteamento interno próprio (RouteRef absoluto
// "endoflife", pro clique num ciclo abrir detalhe) — precisa estar montado
// como Content routável (path próprio), não como Card simples.
// convertLegacyEntityCardExtension não registra rota nenhuma; o componente
// quebrava em runtime com "Routable extension component... was not
// discovered in the app element tree" (achado em prod 2026-07-13, mesmo
// padrão já usado pro ECR).
const endOfLifeContent = convertLegacyEntityContentExtension(EntityEndOfLifeCard, {
  name: 'end-of-life',
  filter: isEndOfLifeAvailable,
  path: '/endoflife',
  title: 'End of Life',
});

// kwirth-log — logs em tempo real do apoiase-stg-cluster-eks via Kwirth
// (namespace kwirth). apiRef/client exportados publicamente, sem deep-import
// hack (diferente de ecr/ecs/badges). enableRestart: false — Kwirth
// deployado só-leitura de propósito (CHANNEL_OPS desligado, sem
// restart/shell), ver docs/operacao.md.
const kwirthLogApi = ApiBlueprint.make({
  name: 'kwirth-log',
  params: define =>
    define(
      createApiFactory({
        api: kwirthLogApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new KwirthLogClient({ discoveryApi, fetchApi }),
      }),
    ),
});

// ponytail: achei que precisava de wrapper pra injetar enableRestart={false}
// (mesmo motivo do badgesCard/lambdaOverviewCard) e usei EntityContentBlueprint
// direto — errado. EntityKwirthLogContent é extensão legada de verdade (usa
// RouteRef absoluto próprio 'kwirthlog' internamente, tipo ECR/end-of-life),
// e só convertLegacyEntityContentExtension faz o bridge desse RouteRef pro
// sistema novo. Sem o bridge: "Routable extension component ... was not
// discovered in the app element tree" (achado em prod 2026-07-14).
// convertLegacyEntityContentExtension não repassa props (LegacyExtension:
// ComponentType<{}>), então enableRestart chega undefined — falsy, mesmo
// efeito prático de false (sem restart), só sem forçar o valor explícito.
// Cast necessário: TS reclama de enableRestart obrigatório vs ComponentType<{}>,
// mas em runtime a prop simplesmente chega undefined, sem erro.
const kwirthLogContent = convertLegacyEntityContentExtension(
  EntityKwirthLogContent as ComponentType<{}>,
  {
    name: 'kwirth-log',
    filter: isKwirthAvailable,
    path: '/kwirthlog',
    title: 'Logs',
  },
);

export const legacyEntityCardsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    jiraApi,
    jiraOverviewCard,
    ecrImagesContent,
    ecrApi,
    ecsServicesContent,
    ecsApi,
    badgesCard,
    badgesApi,
    awsLambdaApi,
    lambdaOverviewCard,
    endOfLifeApi,
    endOfLifeContent,
    kwirthLogApi,
    kwirthLogContent,
  ],
});
